import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { debounce } from 'lodash'
import { trans } from '../../../_providers/Translation'

//partials
import Filter from './partials/Filter'
import Table from './partials/Table'
import SecondaryTabs from './partials/SecondaryTabs'
import AddOrEdit from './partials/modals/AddOrEdit'
import { useHistory, useLocation } from 'react-router-dom'
import Api from '../../../_helpers/Api'
import Pagination from '../../Pagination'
import Question from '../../modals/messages/Question'
import Success from '../../modals/messages/Success'
import Calendar from './partials/Calendar'
import moment from 'moment'
import Help from '../../partials/Help'
import HelpInfo from './partials/HelpInfo'
import '@/assets/scss/app/tray.scss'
import { useNotesContext } from '@/_providers/Notes'
import { enqueueSnackbar } from 'notistack'
import { useSocketContext } from '../../../_providers/Socket'
import { useAuthDataContext } from '../../../_providers/Auth'

function Todo() {

    const history = useHistory()
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)

    const modalAddOrEditRef = useRef(null)
    const modalQuestionRef = useRef(null)
    const notifyModalRef = useRef(null)

    const { handleAddToTray } = useNotesContext()

    const listener = useSocketContext()
    const auth = useAuthDataContext()
    const partnerId = auth.partnerId()

    const tabs = ['all', 'awaiting', 'completed', 'fail', 'calendar']

    const queryClient = useQueryClient()

    const fromGlobalSearch = searchParams.get('from_global_search') || null


    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1)
    const [filter, setFilter] = useState({})



    // Sync filter & page from URL
    useEffect(() => {
        let status = searchParams.get('status')

        setPage(parseInt(searchParams.get('page')) || 1)
        setFilter(prev => ({
            ...prev,
            status: tabs.indexOf(status) > -1 ? status : tabs[0],
            date: searchParams.get('date') || '',
            deadline: searchParams.get('deadline') || '',
            text: searchParams.get('text') || '',
            creator_id: searchParams.get('creator_id') || '',
            performer_id: searchParams.get('performer_id') || '',
            performer_type: searchParams.get('performer_type') || '',
            creator_type: searchParams.get('creator_type') || '',
            priority_id: searchParams.get('priority_id') || '',
            flagged: searchParams.get('flagged') || '',
            completed: searchParams.get('completed') || '',
        }))
    }, [location.search])

    useEffect(() => {
        if (fromGlobalSearch) {
            setFilter(prev => ({
                ...prev,
                text: `id:${fromGlobalSearch}`
            }))
        }
    }, [fromGlobalSearch])

    useEffect(() => {
        if (filter.status === 'calendar') {
            handleRangeChange(
                moment().startOf('month').format('YYYY-MM-DD'),
                moment().endOf('month').format('YYYY-MM-DD')
            )
        }
    }, [filter.status])

    const { data: queryData, isLoading } = useQuery({
        queryKey: ['todos/all', location.search],
        queryFn: async () => {
            const params = new URLSearchParams(location.search)
            let url = 'todos/all?page=' + (parseInt(params.get('page')) || 1)

            Object.entries({
                status: params.get('status') || tabs[0],
                date: params.get('date') || '',
                deadline: params.get('deadline') || '',
                text: params.get('text') || '',
                creator_id: params.get('creator_id') || '',
                priority_id: params.get('priority_id') || '',   
                completed: params.get('completed') || '',
                flagged: params.get('flagged') || '',
                performer_id: params.get('performer_id') || '',
                performer_type: params.get('performer_type') || '',
                creator_type: params.get('creator_type') || '',
            }).forEach(([key, value]) => {
                if (value) url += '&' + key + '=' + value
            })

            const res = await Api.get(url)
            return res.data
        },
        keepPreviousData: true,
    })

    // Backward-compatible todo object for child components
    const todo = {
        loading: isLoading,
        data: queryData?.items || [],
        total: queryData?.total || 0,
        pages: queryData?.pages || 0,
        page: page,
        filter: filter,
    }

    const refreshData = () => {
        queryClient.invalidateQueries(['todos', 'all'])
    }

    // Refresh the table in-place whenever a todo completion status changes.
    // Skipped on the first ("all") and last ("calendar") tabs. Uses a background
    // refetch of the current query so no loading indicator or filter reset occurs.
    useEffect(() => {
        if (!listener || !partnerId) return

        const firstTab = tabs[0]
        const lastTab = tabs[tabs.length - 1]
        if (filter.status === firstTab || filter.status === lastTab) return

        const channel = listener.private(`partners.${partnerId}`)

        const handleCompletionStatusChanged = () => {
            queryClient.invalidateQueries({ queryKey: ['todos/all', location.search] })
        }

        channel.listen('TodoCompletionStatusChanged', handleCompletionStatusChanged)

        return () => {
            channel.stopListening('TodoCompletionStatusChanged', handleCompletionStatusChanged)
        }
    }, [listener, partnerId, filter.status, location.search])

    const pushUrl = (newPage, newFilter) => {
        let url = '?page=' + newPage

        Object.entries(newFilter).forEach(([key, value]) => {
            if (value) {
                url += '&' + key + '=' + value
            }
        })

        history.push(url)
    }

    const handleAdd = () => {
        modalAddOrEditRef.current.add()
    }

    const handleCreate = () => {
        refreshData()
    }

    // update
    const handleEdit = id => {
        modalAddOrEditRef.current.edit(id)
    }

    const handleUpdate = newData => {

        // let data = todo.data.map(i => {
        //     if (i.id == newData.id) {
        //         i = newData;
        //     }
        //     return i;
        // });

        // setTodo(prev => ({
        //     ...prev,
        //     data: data
        // }));

        refreshData()
    }

    // delete
    const handleDelete = id => {

        modalQuestionRef.current.open(id)

        modalQuestionRef.current.onSuccess(() => {
            handleDestroy(id)
        })
    }

    const handleDestroy = id => {
        Api.post('todos/delete', {
            id
        }).then(res => {
            if (res.data.success) {
                refreshData()
            }
        })
    }

    const handleRead = id => {
        Api.post('todos/mark-as-read', {
            id
        }).then(() => {
            refreshData()
            enqueueSnackbar(trans('messages.todoMarkedAsRead'), {
                variant: "info",
            })
        })
    }

    const handleComplete = id => {
        Api.post('todos/mark-as-completed', {
            id
        }).then(() => {
            refreshData()
            enqueueSnackbar(trans('messages.todoMarkedAsCompleted'), {
                variant: "success",
            })
        })
    }

    const handleTogglePause = id => {
        Api.post('todos/toggle-pause', {
            id
        }).then((res) => {
            refreshData()
            enqueueSnackbar(res.data?.paused_at ? trans('messages.todoPaused') : trans('messages.todoResumed'), {
                variant: "success",
            })
        })
    }

    //NEW: Add flag
    const handleFlag = id => {
        Api.post(`todos/${id}/toggle-flag`)
            .then((res) => {
                refreshData()
                enqueueSnackbar(`${trans('messages.todoMarkedAs')} ${res.data?.flagged ? trans('messages.flagged') : trans('messages.unflagged')}.`, {
                    variant: "success",
                })
            })
    }

    const pendingFilter = useRef(filter)

    useEffect(() => {
        pendingFilter.current = filter
    }, [filter])

    const debouncedPushUrl = useCallback(
        debounce(() => {
            pushUrl(1, pendingFilter.current)
        }, 300),
        []
    )

    const handleSearch = (key, val) => {
        const newFilter = { ...pendingFilter.current, [key]: val }
        pendingFilter.current = newFilter
        setFilter(newFilter)       // immediate update → controlled input stays responsive
        debouncedPushUrl()         // debounced URL push → API call is delayed
    }

    const handleRangeChange = (start, end) => {
        pushUrl(1, { ...filter, date: start, deadline: end })
    }

    const handlePage = pageData => {
        pushUrl(pageData.selected + 1, filter)
    }

    const handleTab = tab => {
        pushUrl(1, { ...filter, status: tab })
    }

    const handleNotify = () => {
        Api.post('todos/send-notifications')
            .then(res => {
                notifyModalRef.current.open()
            })
    }

    return (
        <div className="wrapper">

            <Success
                ref={notifyModalRef}
                mainMessage={trans('headings.success1')}
            />

            <AddOrEdit
                ref={modalAddOrEditRef}
                handleCreate={handleCreate}
                handleUpdate={handleUpdate}
            />

            <Question
                ref={modalQuestionRef}
                mainMessage={trans('headings.confirmDelete')}
                secondaryMessage={trans('subheadings.confirmDelete')}
            />

            <div className="top-part">
                <div className="left">
                    <h1 className="page-heading">
                        {trans('headings.todos')} <Help><HelpInfo /></Help>
                    </h1>
                    <p className="page-subheading">
                        {trans('subheadings.todos')}
                    </p>
                </div>
                <div className="right" style={{
                    display: 'flex'
                }}>
                    <button className="button-add" onClick={handleAdd}>
                        {trans('buttons.todo')}
                    </button>
                    {/* <button onClick={handleNotify} style={{
                        marginLeft: '10px'
                    }}>
                        {trans('buttons.sendNotice')}
                    </button> */}
                </div>
            </div>


            <SecondaryTabs
                tab={todo.filter.status}
                handleTab={handleTab}
            />
            <div className='pannel'>
                <h3>
                    {`${trans('filters.allNotes')} (${todo.total || 0})`}
                </h3>
                <Filter
                    data={todo.filter}
                    handleSearch={handleSearch}
                />

                <hr />


                {todo.filter.status === 'calendar'
                    ?
                    <Calendar
                        data={todo.data}
                        start={todo.filter.date}
                        loading={todo.loading}
                        handleRead={handleRead}
                        handleSearch={handleSearch}
                        handleRangeChange={handleRangeChange}
                    />
                    :
                    <>
                        <Table
                            data={todo.data}
                            loading={todo.loading}
                            handleEdit={handleEdit}
                            handleDelete={handleDelete}
                            handleRead={handleRead}
                            handleComplete={handleComplete}
                            handleTogglePause={handleTogglePause}
                            refreshData={refreshData}
                            handleFlag={handleFlag}
                            handleAddToTray={handleAddToTray}
                        />

                        {!todo.loading
                            ?
                            <Pagination
                                pages={todo.pages}
                                page={todo.page}
                                handlePage={handlePage}
                            />
                            :
                            ''
                        }
                    </>
                }
            </div>


        </div>
    )
}

export default Todo